import { Check } from '@mui/icons-material';
import { Button, TextField } from '@mui/material';
import { CodeEvent } from 'domain/codeEvent/CodeEvent';
import { ICodeEventRepository } from 'domain/codeEvent/CodeEvent.repository';
import { ValidationStringLengthEnum } from 'domain/enums/ValidationStringLengthEnum';
import { InputWrapper } from 'primary/Components/Input/InputWrapper';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useTranslate, UseTranslateReturn } from 'primary/hooks/useTranslate';
import { useYupValidationResolver } from 'primary/hooks/useYupValidationResolver';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

interface CreateCodeEventFormProps {
  eventId?: number;
  onSubmitted: (codeEvent: CodeEvent) => void;
  onCancel: () => void;
}

export type CreateCodeEventsForm = {
  code: string;
  eventId: number;
  expireDate?: Date;
};

const validationSchema = (t: UseTranslateReturn) =>
  yup.object({
    code: yup
      .string()
      .max(
        ValidationStringLengthEnum.DEFAULT,
        t('general.forms.errors.tooLong', {
          maxChar: ValidationStringLengthEnum.DEFAULT,
        }),
      )
      .required(t('general.forms.errors.required')),
    eventId: yup.string().required(t('general.forms.errors.required')),
    expireDate: yup.string().nullable(),
  });

const onSubmitCreateCodeEvent =
  (
    codeEventRepository: ICodeEventRepository,
    onSubmitted: (codeEvent: CodeEvent) => void,
    reset: () => void,
    t: UseTranslateReturn,
    idAnnonce?: number,
  ) =>
  (data: CreateCodeEventsForm) => {
    return codeEventRepository.create(data).then((codeEvent: CodeEvent) => {
      sendEventToastMessage(t('event.codeEvent.create.succeed'), 'success');
      reset();
      onSubmitted(codeEvent);
    });
  };

export const CreateCodeEventForm = ({
  eventId,
  onSubmitted,
  onCancel,
}: CreateCodeEventFormProps) => {
  const t = useTranslate();
  const { codeEventRepository, authRepository } = useContextDependency();
  const resolver = useYupValidationResolver(validationSchema);
  const {
    handleSubmit,
    control,
    reset,
    trigger,
    register,
    formState: { isSubmitting, errors },
  } = useForm<CreateCodeEventsForm>({
    resolver,
    defaultValues: {
      eventId: eventId,
      code: '',
    },
  });
  const onSubmit = onSubmitCreateCodeEvent(codeEventRepository, onSubmitted, reset, t);

  if (!eventId) return null;
  return (
    <form className={'-addCodeEventform'} onSubmit={handleSubmit(onSubmit)}>
      <h5>Ajouter un code</h5>
      <div className={'-row'}>
        <div className={'-fields'}>
          <InputWrapper
            noFullWidth
            className={'unpy-form__field -eventCode'}
            label={t('event.codeEvent.form.code.label')}
            errors={errors}
            Input={TextField}
            isSubmitting={false}
            required
            inputProps={{
              ...register('code'),
            }}
          />
          <InputWrapper
            className={'unpy-form__field'}
            label={t('event.codeEvent.form.expireDate.label')}
            errors={errors}
            isSubmitting={isSubmitting}
            Input={TextField}
            inputProps={{
              disabled: isSubmitting,
              ...register('expireDate', { required: false }),
              type: 'datetime-local',
            }}
          />
        </div>
        <div className={'-buttons'}>
          <Button
            variant={'outlined'}
            color={'primary'}
            disabled={isSubmitting}
            type={'submit'}
          >
            <Check />
          </Button>
        </div>
      </div>
    </form>
  );
};
